import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TimesheetDayLockService, {
  DateLock,
  DateLockCreateResult,
  DateLockDeleteResult,
  DateLockPresetResult,
  DayLocksPayload,
  DayLocksSaveResult,
  NewDateLock,
} from './TimesheetDayLockService';
import { weekdayOfDateKey } from './dayLockHelpers';

export const DAY_LOCKS_QUERY_KEY = ['timesheetDayLocks'] as const;

const NO_LOCKS: DayLocksPayload = { lockedWeekdays: [], lockedDates: [] };
const ISO_DATE_KEY = /^\d{4}-\d{2}-\d{2}/;

/**
 * The cached lock payload, whatever its shape: a bare weekday array (what the service resolved
 * before date locks, and what older test mocks still resolve) is weekdays with no date locks.
 */
export function normaliseDayLocks(data: unknown): DayLocksPayload {
  if (Array.isArray(data)) return { lockedWeekdays: data as number[], lockedDates: [] };
  const payload = data as Partial<DayLocksPayload> | null | undefined;
  if (!payload || typeof payload !== 'object') return NO_LOCKS;
  return {
    lockedWeekdays: Array.isArray(payload.lockedWeekdays) ? payload.lockedWeekdays : [],
    lockedDates: Array.isArray(payload.lockedDates) ? payload.lockedDates : [],
  };
}

/** `MM-dd` for an annual lock, keyed so a `yyyy-MM-dd` cell key matches it by slicing. */
function monthDayKey(month: number, day: number): string {
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The firm's locked weekdays and dates (plans/todo/timesheet-day-locks.md and
 * plans/todo/timesheet-date-locks.md, F1).
 *
 * - Read through react-query and refreshed on window focus, so a tab left open picks up an admin's
 *   change; `refresh()` is also called whenever the server answers "day locked" / "not locked".
 * - After an admin saves, the server's fresh locks are written straight into the cache — the grid
 *   updates with no reload.
 * - While loading (or with `enabled` false) nothing is locked: the server stays the authority, and
 *   with the feature flag off no request is made at all.
 * - A date matches a weekday lock, a one-off date lock, or an annual lock on its month/day — read
 *   from the `yyyy-MM-dd` key itself, never a UTC parse (plan hazard HD3).
 */
export function useDayLocks(enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: DAY_LOCKS_QUERY_KEY,
    queryFn: () => TimesheetDayLockService.getLockedWeekdays(),
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const payload = useMemo(() => (enabled ? normaliseDayLocks(query.data) : NO_LOCKS), [enabled, query.data]);
  const { lockedWeekdays, lockedDates } = payload;
  const lockedSet = useMemo(() => new Set(lockedWeekdays), [lockedWeekdays]);
  const { oneOffs, annual } = useMemo(() => {
    const byDate = new Map<string, DateLock>();
    const byMonthDay = new Map<string, DateLock>();
    lockedDates.forEach((lock) => {
      if (lock.date) byDate.set(lock.date.slice(0, 10), lock);
      else if (lock.month && lock.day) byMonthDay.set(monthDayKey(lock.month, lock.day), lock);
    });
    return { oneOffs: byDate, annual: byMonthDay };
  }, [lockedDates]);

  /** The one-off and annual date locks on a `yyyy-MM-dd` day (either may be undefined). */
  const dateLocksFor = useCallback(
    (dateKey: string): { oneOff?: DateLock; annual?: DateLock } => {
      if (!ISO_DATE_KEY.test(dateKey)) return {};
      return { oneOff: oneOffs.get(dateKey.slice(0, 10)), annual: annual.get(dateKey.slice(5, 10)) };
    },
    [oneOffs, annual],
  );

  const isDayLocked = useCallback(
    (dateKey: string): boolean => {
      if (lockedSet.size === 0 && oneOffs.size === 0 && annual.size === 0) return false;
      const { oneOff, annual: yearly } = dateLocksFor(dateKey);
      if (oneOff || yearly) return true;
      const weekday = weekdayOfDateKey(dateKey);
      return weekday !== null && lockedSet.has(weekday);
    },
    [lockedSet, oneOffs, annual, dateLocksFor],
  );

  /** The label of the date lock on this day (one-off before annual, as the server words it), or
   * null when no date lock covers it — a weekday lock has no label. */
  const lockLabelFor = useCallback(
    (dateKey: string): string | null => {
      const { oneOff, annual: yearly } = dateLocksFor(dateKey);
      return (oneOff ?? yearly)?.label ?? null;
    },
    [dateLocksFor],
  );

  const isWeekdayLocked = useCallback((weekday: number) => lockedSet.has(weekday), [lockedSet]);

  const writeCache = useCallback(
    (patch: Partial<DayLocksPayload>) => {
      queryClient.setQueryData(DAY_LOCKS_QUERY_KEY, (prev: unknown) => ({ ...normaliseDayLocks(prev), ...patch }));
    },
    [queryClient],
  );

  // 409: another admin changed the locks — show the real state before anyone retries.
  const refetchOnConflict = useCallback(
    (err: unknown) => {
      if ((err as { status?: number } | null)?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: DAY_LOCKS_QUERY_KEY });
      }
    },
    [queryClient],
  );

  const setLockedWeekdays = useCallback(
    async (weekdays: number[]): Promise<DayLocksSaveResult> => {
      try {
        const result = await TimesheetDayLockService.setLockedWeekdays(weekdays, lockedWeekdays);
        writeCache({ lockedWeekdays: result.lockedWeekdays });
        return result;
      } catch (err) {
        refetchOnConflict(err);
        throw err;
      }
    },
    [lockedWeekdays, writeCache, refetchOnConflict],
  );

  const addDateLock = useCallback(
    async (lock: NewDateLock): Promise<DateLockCreateResult> => {
      try {
        const result = await TimesheetDayLockService.addDateLock(lock);
        writeCache({ lockedDates: result.lockedDates });
        return result;
      } catch (err) {
        refetchOnConflict(err);
        throw err;
      }
    },
    [writeCache, refetchOnConflict],
  );

  const deleteDateLock = useCallback(
    async (lockId: string): Promise<DateLockDeleteResult> => {
      try {
        const result = await TimesheetDayLockService.deleteDateLock(lockId);
        writeCache({ lockedDates: result.lockedDates });
        return result;
      } catch (err) {
        // 404: another admin removed it already — show the real state.
        void queryClient.invalidateQueries({ queryKey: DAY_LOCKS_QUERY_KEY });
        throw err;
      }
    },
    [queryClient, writeCache],
  );

  const applyPreset = useCallback(
    async (preset: string, year: number): Promise<DateLockPresetResult> => {
      const result = await TimesheetDayLockService.applyPreset(preset, year);
      writeCache({ lockedDates: result.lockedDates });
      return result;
    },
    [writeCache],
  );

  const refresh = useCallback(() => {
    if (!enabled) return;
    void queryClient.invalidateQueries({ queryKey: DAY_LOCKS_QUERY_KEY });
  }, [enabled, queryClient]);

  return {
    /** True once the locks have loaded — admin toggles stay disabled until then, so a PUT can never
     * be built from an empty placeholder list. */
    isReady: enabled && query.isSuccess,
    lockedWeekdays,
    lockedDates,
    /** Any lock at all — weekday or date. Gates the permish 2/3 lock UI (plan hazard HD4). */
    hasLocks: lockedWeekdays.length > 0 || lockedDates.length > 0,
    isDayLocked,
    isWeekdayLocked,
    dateLocksFor,
    lockLabelFor,
    setLockedWeekdays,
    addDateLock,
    deleteDateLock,
    applyPreset,
    refresh,
  };
}
